import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '@/app/store';
import { Product, ProductState, ProductsWithLocations } from './materials.interface';

// Thunks para Materiales
import * as thunks from './materialsThunks';
import { APIResponse } from '@/types/custom-http';


const initialState: ProductState = {
  items: [],
  productUbications: [],
  selectedProduct: null,
  loading: false,
  error: null,
};



// Slice
const productsSlice = createSlice({
  name: 'products',
  initialState,
  reducers: {
    clearProducts: (state) => {
      state.items = [];
    },
    clearSelectedProduct: (state) => {
      state.selectedProduct = null;
    },
    clearProductError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Obtener todos los productos
      .addCase(thunks.fetchProducts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(thunks.fetchProducts.fulfilled, (state, action: PayloadAction<APIResponse<{page: number; size: number; total: number; totalPages: number; productos: Product[]}>>) => {
        state.loading = false;
        if(action.payload.data){
          state.items = action.payload.data.productos;
        }
      })
      .addCase(thunks.fetchProducts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message as string;
      })

      // Obtener un producto específico por ID
      .addCase(thunks.fetchProductById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(thunks.fetchProductById.fulfilled, (state, action: PayloadAction<APIResponse<Product>>) => {
        state.loading = false;
        if(action.payload.data){
          state.selectedProduct = action.payload.data;
        }
      })
      .addCase(thunks.fetchProductById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message as string;
      })

      // Crear un nuevo producto
      .addCase(thunks.createProduct.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(thunks.createProduct.fulfilled, (state, action: PayloadAction<APIResponse<Product>>) => {
        state.loading = false;
        if(action.payload.data){
          state.items.push(action.payload.data);
        }
      })
      .addCase(thunks.createProduct.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message as string;
      })

      // Actualizar datos de un producto
      .addCase(thunks.updateProduct.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(thunks.updateProduct.fulfilled, (state, action: PayloadAction<APIResponse<Product>>) => {
        state.loading = false;
        const index = state.items.findIndex(item => item.id === action.payload.data?.id);
        if (index !== -1 && action.payload.data) {
          state.items[index] = action.payload.data;
        }
        if (state.selectedProduct && state.selectedProduct.id === action.payload.data?.id) {
          state.selectedProduct = action.payload.data;
        }
      })
      .addCase(thunks.updateProduct.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message as string;
      })

      // Eliminar un producto
      .addCase(thunks.deleteProduct.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(thunks.deleteProduct.fulfilled, (state, action) => {
        state.loading = false;
        const deletedProductId = action.payload;
        
        state.items = state.items.filter(item => item.id !== deletedProductId);
        
        if (state.selectedProduct && state.selectedProduct.id === deletedProductId) {
          state.selectedProduct = null;
        }
      })
      .addCase(thunks.deleteProduct.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message as string;
      })

      // Obtener los productos con ubicaciones
      .addCase(thunks.fetchProductUbications.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(thunks.fetchProductUbications.fulfilled, (state, action: PayloadAction<APIResponse<{page: number; size: number; total: number; totalPages: number; items: ProductsWithLocations[]}>>) => {
        state.loading = false;
        if(action.payload.data){
          state.productUbications = action.payload.data.items;
        }
      })
      .addCase(thunks.fetchProductUbications.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message as string;
      })
      
  },
});

// Selectors
export const selectAllProducts = (state: RootState) => state.products.items;
export const selectProductById = (state: RootState, id: number) => state.products.items.find(item => item.id === id);
export const selectProductLoading = (state: RootState) => state.products.loading;
export const selectProductError = (state: RootState) => state.products.error;

// Actions
export const { clearProducts, clearSelectedProduct,  clearProductError} = productsSlice.actions;

export default productsSlice.reducer;